import {
  cloneElement, isValidElement, useMemo, useState,
  type CSSProperties, type ReactElement, type ReactNode,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { cn } from './cn';
import { useWheelScroll } from './useWheelScroll';

/**
 * Ant Design `AutoComplete` replacement — a free-text input with a suggestion
 * dropdown. Unlike Select, the typed text IS the value. Supports a custom child
 * input (`<AutoComplete><Input/></AutoComplete>`), filterOption, onSelect/onSearch.
 */
interface Opt { value: string | number; label?: ReactNode }

export interface AutoCompleteProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onSelect?: (value: string, option?: any) => void;
  onSearch?: (value: string) => void;
  options?: Opt[];
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  filterOption?: boolean | ((input: string, option: Opt) => boolean);
  notFoundContent?: ReactNode;
  style?: CSSProperties;
  className?: string;
  children?: ReactElement;
  /** Names the control where no visible label points at it (search bars, map pickers). */
  'aria-label'?: string;
}

export function AutoComplete({
  value, defaultValue, onChange, onSelect, onSearch, options = [], placeholder,
  allowClear, disabled, filterOption = true, notFoundContent, style, className, children,
  'aria-label': ariaLabel,
}: AutoCompleteProps) {
  const [inner, setInner] = useState(defaultValue ?? '');
  const val = value !== undefined ? value : inner;
  const [open, setOpen] = useState(false);
  // Wheel-scroll the suggestion list even inside a modal (whose scroll-lock eats wheel events over
  // this portalled popover). See useWheelScroll.
  const attachWheel = useWheelScroll<HTMLDivElement>();

  const filtered = useMemo(() => {
    if (filterOption === false) return options;
    if (typeof filterOption === 'function') return options.filter((o) => filterOption(val ?? '', o));
    const q = (val ?? '').toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      `${o.label ?? o.value}`.toLowerCase().includes(q) || String(o.value).toLowerCase().includes(q));
  }, [options, val, filterOption]);

  const setVal = (v: string) => { if (value === undefined) setInner(v); onChange?.(v); onSearch?.(v); setOpen(true); };
  const choose = (o: Opt) => { const v = String(o.value); if (value === undefined) setInner(v); onChange?.(v); onSelect?.(v, o); setOpen(false); };

  const inputEl = children && isValidElement(children)
    ? cloneElement(children as ReactElement<any>, {
        value: val,
        onChange: (e: any) => setVal(typeof e === 'string' ? e : e?.target?.value ?? ''),
        onFocus: (e: any) => { setOpen(true); (children.props as any).onFocus?.(e); },
        disabled,
        ...((children.props as any)['aria-label'] == null && ariaLabel ? { 'aria-label': ariaLabel } : null),
      })
    : (
      <input
        value={val ?? ''}
        onChange={(e) => setVal(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        disabled={disabled}
        className="h-9 w-full rounded-control border border-line bg-surface px-3 pr-7 text-sm text-content outline-none transition-colors hover:border-brand-300 focus-visible:border-brand-400 focus-visible:ring-2 focus-visible:ring-brand-500/20 placeholder:text-subtle disabled:opacity-60"
      />
    );

  const showList = filtered.length > 0 || notFoundContent != null;

  return (
    <Popover.Root open={open && !disabled && showList} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div className={cn('relative', className)} style={style} onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
          {inputEl}
          {allowClear && val && !disabled && (
            <button type="button" aria-label="Clear" onClick={() => setVal('')} className="absolute right-2 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-full text-subtle hover:bg-surface-2 hover:text-content">×</button>
          )}
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          ref={attachWheel}
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="z-[var(--z-popover)] max-h-64 w-[var(--radix-popover-trigger-width)] overflow-auto overscroll-contain rounded-card border border-line bg-surface p-1.5 shadow-pop"
        >
          {filtered.length === 0 ? (
            <div className="px-2.5 py-3 text-center text-sm text-subtle">{notFoundContent}</div>
          ) : (
            filtered.map((o, idx) => (
              <div
                key={`${o.value}-${idx}`}
                role="option"
                onMouseDown={(e) => { e.preventDefault(); choose(o); }}
                className="cursor-pointer select-none rounded-control px-2.5 py-2 text-sm text-content hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/30 dark:hover:text-brand-300"
              >
                {o.label ?? String(o.value)}
              </div>
            ))
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
